import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import DonorRegistration from '../components/DonorRegistration';

/* ── Animated blood drop SVG ── */
const BloodDrop = ({ className = '', style = {} }) => (
  <svg viewBox="0 0 60 80" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} style={style}>
    <path d="M30 4 C30 4 6 34 6 52 C6 65.25 17 76 30 76 C43 76 54 65.25 54 52 C54 34 30 4 30 4Z"
      fill="url(#dropGrad)" opacity="0.92" />
    <path d="M30 18 C30 18 14 40 14 52 C14 61 21.2 68 30 68 C38.8 68 46 61 46 52 C46 40 30 18 30 18Z"
      fill="rgba(255,255,255,0.15)" />
    <defs>
      <linearGradient id="dropGrad" x1="30" y1="4" x2="30" y2="76" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stopColor="#f09a72" />
        <stop offset="100%" stopColor="#8f2a0e" />
      </linearGradient>
    </defs>
  </svg>
);

/* ── Floating particle ── */
const Particle = ({ style }) => (
  <div className="absolute rounded-full opacity-20 animate-float" style={style} />
);

/* ── Benefit card ── */
const Benefit = ({ icon, title, desc, delay }) => (
  <div
    className="flex items-start gap-3 p-4 rounded-2xl animate-fadeInUp"
    style={{
      background: 'rgba(255,255,255,0.08)',
      border: '1px solid rgba(255,255,255,0.14)',
      backdropFilter: 'blur(8px)',
      animationDelay: delay,
      animationFillMode: 'both',
    }}
  >
    <span className="text-2xl shrink-0 mt-0.5">{icon}</span>
    <div>
      <p className="text-sm font-bold text-white leading-tight">{title}</p>
      <p className="text-xs mt-0.5 leading-relaxed" style={{ color: 'rgba(255,255,255,0.68)' }}>{desc}</p>
    </div>
  </div>
);

/* ══════════════════════════════════════════════════════════════
   Main Page
══════════════════════════════════════════════════════════════ */
const RegisterDonorPage = ({ onSuccess }) => {
  const [searchParams] = useSearchParams();
  const [showForm, setShowForm] = useState(false);
  const [entered, setEntered] = useState(false);

  const isQRFlow = searchParams.get('ref') === 'qr';
  const qrLabel = searchParams.get('label') || '';

  /* Non-QR flows skip splash and go straight to form */
  useEffect(() => {
    if (!isQRFlow) setShowForm(true);
  }, [isQRFlow]);

  const handleEnter = () => {
    setEntered(true);
    setTimeout(() => setShowForm(true), 600);
  };

  /* ── Form view ── */
  if (showForm) {
    return <DonorRegistration onSuccess={onSuccess} />;
  }

  /* ── Splash view ── */
  return (
    <div
      className="relative min-h-screen flex flex-col overflow-hidden"
      style={{ background: 'linear-gradient(160deg, #2e1409 0%, #5c3a1e 25%, #8f2a0e 60%, #c4451a 100%)' }}
    >
      {/* Background dot pattern */}
      <div className="absolute inset-0 pointer-events-none"
        style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.07) 1px, transparent 0)', backgroundSize: '28px 28px' }} />

      {/* Radial glow */}
      <div className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse at 50% 30%, rgba(196,69,26,0.45) 0%, transparent 65%)' }} />

      {/* Floating particles */}
      {[
        { width: 12, height: 12, top: '8%',  left: '12%', background: '#f09a72', animationDelay: '0s',   animationDuration: '4s'  },
        { width: 8,  height: 8,  top: '18%', left: '82%', background: '#e8c47a', animationDelay: '1s',   animationDuration: '5s'  },
        { width: 16, height: 16, top: '55%', left: '6%',  background: '#c4451a', animationDelay: '0.5s', animationDuration: '3.5s'},
        { width: 10, height: 10, top: '70%', left: '88%', background: '#f09a72', animationDelay: '1.5s', animationDuration: '4.5s'},
        { width: 6,  height: 6,  top: '40%', left: '92%', background: '#e8c47a', animationDelay: '2s',   animationDuration: '6s'  },
        { width: 14, height: 14, top: '85%', left: '20%', background: '#c4451a', animationDelay: '0.8s', animationDuration: '5s'  },
      ].map((p, i) => (
        <Particle key={i} style={{ width: p.width, height: p.height, top: p.top, left: p.left, background: p.background, animationDelay: p.animationDelay, animationDuration: p.animationDuration }} />
      ))}

      {/* Content */}
      <div
        className="relative flex flex-col items-center justify-center flex-1 px-6 py-12 transition-all duration-600"
        style={{ opacity: entered ? 0 : 1, transform: entered ? 'scale(0.96)' : 'scale(1)', transition: 'opacity 0.6s ease, transform 0.6s ease' }}
      >
        {/* OCYM Logo + Brand */}
        <div className="flex flex-col items-center mb-8 animate-fadeInUp" style={{ animationDelay: '0.1s', animationFillMode: 'both' }}>
          <div className="relative mb-4">
            {/* Glow ring */}
            <div className="absolute inset-0 rounded-full animate-pulse-crimson" style={{ background: 'rgba(196,69,26,0.3)', transform: 'scale(1.4)', borderRadius: '50%' }} />
            <img
              src="/ocym-logo.png"
              alt="OCYM Logo"
              className="w-20 h-20 object-contain drop-shadow-2xl relative z-10 animate-float"
            />
          </div>

          {/* Brand name */}
          <h1 className="text-3xl font-black text-white text-center leading-tight tracking-tight"
            style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', textShadow: '0 4px 20px rgba(0,0,0,0.4)' }}>
            OCYM Raktadhan
          </h1>
          <p className="text-2xl font-bold mt-1 text-center animate-float"
            style={{ fontFamily: 'Noto Sans Devanagari, sans-serif', color: '#f09a72', animationDelay: '0.5s', textShadow: '0 2px 12px rgba(0,0,0,0.3)' }}>
            रक्तदान
          </p>
          <p className="text-sm mt-2 text-center font-medium tracking-widest uppercase"
            style={{ color: 'rgba(255,255,255,0.55)', letterSpacing: '0.18em' }}>
            Kerala's Blood Donors Network
          </p>
        </div>

        {/* Blood drops row */}
        <div className="flex items-end gap-3 mb-8 animate-fadeInUp" style={{ animationDelay: '0.25s', animationFillMode: 'both' }}>
          {[
            { h: 'h-8',  delay: '0s',    opacity: 0.5 },
            { h: 'h-12', delay: '0.15s', opacity: 0.75 },
            { h: 'h-16', delay: '0.3s',  opacity: 1 },
            { h: 'h-12', delay: '0.15s', opacity: 0.75 },
            { h: 'h-8',  delay: '0s',    opacity: 0.5 },
          ].map((d, i) => (
            <BloodDrop key={i} className={`w-6 ${d.h} animate-float`} style={{ opacity: d.opacity, animationDelay: d.delay }} />
          ))}
        </div>

        {/* Welcome headline */}
        <div className="text-center mb-6 animate-fadeInUp" style={{ animationDelay: '0.35s', animationFillMode: 'both' }}>
          <h2 className="text-xl font-black text-white leading-tight mb-2"
            style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', textShadow: '0 2px 12px rgba(0,0,0,0.3)' }}>
            {qrLabel ? `Welcome — ${qrLabel}` : 'Be a Hero. Give Life.'}
          </h2>
          <p className="text-base leading-relaxed max-w-xs mx-auto"
            style={{ color: 'rgba(255,255,255,0.75)' }}>
            One donation. Up to <strong className="text-white">3 lives saved</strong>.<br />
            The noblest gift you can give is life itself.
          </p>
        </div>

        {/* Benefits */}
        <div className="w-full max-w-sm space-y-3 mb-10">
          <Benefit icon="🩸" title="Save up to 3 lives per donation"   desc="A single blood donation can help multiple patients in critical need."            delay="0.45s" />
          <Benefit icon="❤️" title="A sacred act of compassion"         desc="Donating blood is one of the most selfless and noble things you can do."          delay="0.55s" />
          <Benefit icon="🏥" title="Always needed, never wasted"        desc="Blood cannot be manufactured — only generous donors like you can provide it."      delay="0.65s" />
          <Benefit icon="🤝" title="Join the OCYM community"            desc="Be part of a network of dedicated donors across all 14 districts of Kerala."       delay="0.75s" />
        </div>

        {/* Enter CTA */}
        <div className="w-full max-w-sm animate-fadeInUp" style={{ animationDelay: '0.85s', animationFillMode: 'both' }}>
          <button
            onClick={handleEnter}
            className="w-full relative overflow-hidden rounded-2xl font-black text-lg py-4 transition-all duration-200 active:scale-95 hover:-translate-y-0.5"
            style={{
              background: 'linear-gradient(135deg, #f9efd5 0%, #e8c47a 50%, #d4a96a 100%)',
              color: '#5c3a1e',
              boxShadow: '0 8px 32px rgba(0,0,0,0.35), 0 0 0 2px rgba(255,255,255,0.2)',
              fontFamily: 'Plus Jakarta Sans, sans-serif',
            }}
          >
            <span className="relative z-10 flex items-center justify-center gap-2">
              <BloodDrop className="w-5 h-6" />
              Register as a Donor
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </span>
          </button>

          <p className="text-center text-xs mt-4" style={{ color: 'rgba(255,255,255,0.45)' }}>
            Free · Voluntary · Confidential
          </p>
        </div>

        {/* Bottom quote */}
        <div className="mt-8 text-center max-w-xs animate-fadeInUp" style={{ animationDelay: '1s', animationFillMode: 'both' }}>
          <p className="text-xs italic leading-relaxed" style={{ color: 'rgba(255,255,255,0.4)' }}>
            "The blood you donate gives someone else another chance at life."
          </p>
        </div>
      </div>

      {/* Bottom safe-area spacer for notched phones */}
      <div className="safe-area-pb" />
    </div>
  );
};

export default RegisterDonorPage;
